import { FamilyMemberHistory, Patient } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { NumberInput, Select, TextInput, Textarea } from '@mantine/core'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { useState } from 'react'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'relationship', title: 'Family Member' },
  { code: 'doctor', title: 'Doctor' },
  { code: 'condition', title: 'Problem(s)' },
]

const RELATIONSHIP_OPTIONS = [
  { value: 'MTH', label: 'Mother' },
  { value: 'FTH', label: 'Father' },
  { value: 'SIB', label: 'Sibling' },
  { value: 'BRO', label: 'Brother' },
  { value: 'SIS', label: 'Sister' },
  { value: 'SON', label: 'Son' },
  { value: 'DAU', label: 'Daughter' },
  { value: 'GRMTH', label: 'Grandmother' },
  { value: 'GRFTH', label: 'Grandfather' },
  { value: 'AUNT', label: 'Aunt' },
  { value: 'UNCLE', label: 'Uncle' },
  { value: 'COUSN', label: 'Cousin' },
]

const STATUS_OPTIONS: { value: NonNullable<FamilyMemberHistory['status']>; label: string }[] = [
  { value: 'partial', label: 'Partial' },
  { value: 'completed', label: 'Completed' },
  { value: 'entered-in-error', label: 'Entered in Error' },
  { value: 'health-unknown', label: 'Health Unknown' },
]

const getDoctor = (history: FamilyMemberHistory) => {
  if (Array.isArray(history.extension)) {
    const doctorExt = history.extension.find(
      (e) =>
        e.url?.toLowerCase().includes('doctor') ||
        e.url?.toLowerCase().includes('practitioner') ||
        e.url?.toLowerCase().includes('provider'),
    )
    if (doctorExt?.valueReference?.display) return doctorExt.valueReference.display
    if (doctorExt?.valueReference?.reference) return doctorExt.valueReference.reference
  }
  return ''
}

const renderColumn = (history: FamilyMemberHistory, itemCode: string) => {
  switch (itemCode) {
    case 'relationship':
      return (
        <div>
          <div>
            {history.name ||
              history.relationship?.text ||
              history.relationship?.coding?.[0]?.display ||
              '-'}
          </div>
          {history.deceasedBoolean && <div className="text-xs text-gray-400">Deceased</div>}
        </div>
      )
    case 'doctor':
      return getDoctor(history)
    case 'condition':
      return (
        <div>
          {history.condition?.length
            ? history.condition.map((item, idx) => <div key={idx}>{item.code?.text || '-'}</div>)
            : 'n/a'}
        </div>
      )
    default:
      return ''
  }
}

export const FamilyHistory = ({ patient, item }: Props) => {
  const [relationship, setRelationship] = useState<string | null>(null)
  const [conditionText, setConditionText] = useState('')
  const [ageValue, setAgeValue] = useState<number | string>('')
  const [status, setStatus] = useState<string | null>('completed')
  const [note, setNote] = useState('')

  const resetForm = () => {
    setRelationship(null)
    setConditionText('')
    setAgeValue('')
    setStatus('completed')
    setNote('')
  }

  const buildResource = (): FamilyMemberHistory | { error: string } => {
    if (!relationship) return { error: 'Please select a relationship.' }

    const relationshipOption = RELATIONSHIP_OPTIONS.find((r) => r.value === relationship)
    const conditions = conditionText
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)

    return {
      resourceType: 'FamilyMemberHistory',
      status: (status || 'completed') as NonNullable<FamilyMemberHistory['status']>,
      patient: { reference: `Patient/${patient.id}` },
      date: new Date().toISOString(),
      relationship: {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: relationship,
            display: relationshipOption?.label || relationship,
          },
        ],
        text: relationshipOption?.label || relationship,
      },
      ageAge:
        typeof ageValue === 'number'
          ? { value: ageValue, unit: 'years', system: 'http://unitsofmeasure.org', code: 'a' }
          : undefined,
      condition: conditions.length ? conditions.map((c) => ({ code: { text: c } })) : undefined,
      note: note.trim() ? [{ text: note.trim() }] : undefined,
    }
  }

  return (
    <AddResourceSection<FamilyMemberHistory>
      patient={patient}
      title="Family History"
      modalTitle="Add Family History"
      resourceType="FamilyMemberHistory"
      featureContext="FamilyMemberHistory"
      noun="family history"
      isSubmitDisabled={!relationship}
      resetForm={resetForm}
      buildResource={buildResource}
      renderForm={() => (
        <>
          <Select
            label="Relationship *"
            data={RELATIONSHIP_OPTIONS}
            value={relationship}
            onChange={setRelationship}
            searchable
            placeholder="Select relationship"
          />
          <TextInput
            label="Condition(s)"
            description="Comma-separated, e.g. 'Diabetes, Hypertension'"
            placeholder="e.g. Diabetes, Hypertension"
            value={conditionText}
            onChange={(e) => setConditionText(e.currentTarget.value)}
          />
          <NumberInput
            label="Age (years)"
            value={ageValue}
            onChange={setAgeValue}
            min={0}
            max={130}
            placeholder="Optional"
          />
          <Select label="Status" data={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <Textarea
            label="Notes"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder="Optional notes"
            minRows={2}
          />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="date"
          blockRemove
        />
      )}
    </AddResourceSection>
  )
}
